import { Stream } from '../stream';

const MAX_BUFFERED_AMOUNT = 64 * 1024;
const CHANNEL_CLOSING_TIMEOUT = 5 * 1000;

export class ChannelManager extends Stream<'data' | 'open'> {
  private channel?: RTCDataChannel; // Mark as optional since it isn't immediately assigned for receivers
  private closingInterval: any;
  private interval: any;

  constructor(
    private peerConnection: RTCPeerConnection,
    private isInitiator: boolean,
    private channelNegotiated: boolean,
    private channelName: string,
    private channelConfig?: RTCDataChannelInit,
  ) {
    super();

    if (this.isInitiator || this.channelNegotiated) {
      this.onDataChannel({
        channel: this.peerConnection.createDataChannel(this.channelName, this.channelConfig),
      } as RTCDataChannelEvent);
    } else {
      this.peerConnection.addEventListener('datachannel', this.onDataChannel);
    }
  }

  private onDataChannel = (event: RTCDataChannelEvent) => {
    if (!event.channel) {
      this.destroy('Data channel event is missing `channel` property');
      return;
    }

    // FIX 3: Prevent memory leaks from multiple incoming channels overwriting our state
    if (this.channel) return;

    // Only bind to the channel name we are expecting (if we are the receiver)
    if (!this.isInitiator && !this.channelNegotiated && event.channel.label !== this.channelName) {
      return;
    }

    this.channel = event.channel;
    this.channel.binaryType = 'arraybuffer';

    if (typeof this.channel.bufferedAmountLowThreshold === 'number') {
      this.channel.bufferedAmountLowThreshold = MAX_BUFFERED_AMOUNT;
    }

    this.channelName = this.channel.label;

    this.channel.addEventListener('message', this.onChannelMessage);
    this.channel.addEventListener('bufferedamountlow', this.onChannelBufferedAmountLow);
    this.channel.addEventListener('open', this.onChannelOpen);
    this.channel.addEventListener('close', this.onChannelClose);
    this.channel.addEventListener('error', this.onChannelError);

    let isClosing = false;

    // Ensure we don't leak an interval just in case
    if (this.closingInterval) clearInterval(this.closingInterval);

    this.closingInterval = setInterval(() => {
      if (this.channel && this.channel.readyState === 'closing') {
        if (isClosing) this.onChannelClose();
        isClosing = true;
      } else {
        isClosing = false;
      }
    }, CHANNEL_CLOSING_TIMEOUT);
  };

  private onChannelMessage = (event: MessageEvent) => {
    if (this.isDestroyedOrDestroying()) return;
    let { data } = event;
    if (data instanceof ArrayBuffer) data = new Uint8Array(data);
    this.emit('data', data);
  };

  private onChannelBufferedAmountLow = () => {
    // Buffer has drained enough to send more data
  };

  get bufferSize() {
    return (this.channel && this.channel.bufferedAmount) || 0;
  }

  // FIX 1: Null safety for when receiver hasn't gotten the channel yet
  public isOpen() {
    return this.channel?.readyState === 'open';
  }

  private onChannelOpen = () => {
    if (this.isDestroyedOrDestroying()) {
      return;
    }
    this.emit('open');

    if (typeof this.channel?.bufferedAmountLowThreshold !== 'number') {
      if (this.interval) clearInterval(this.interval);
      this.interval = setInterval(() => this.onInterval(), 150);
      if (this.interval.unref) this.interval.unref();
    }
  };

  private onChannelClose = () => {
    if (this.isDestroyedOrDestroying()) {
      return;
    }
    this.destroy();
  };

  private onChannelError = (event: Event) => {
    // @ts-ignore
    const err = event.error instanceof Error ? event.error : new Error(`Datachannel error: ${event.message} ${event.filename}:${event.lineno}:${event.colno}`);
    this.destroy(err);
  };

  // FIX 2: Fixed TypeScript signature to allow binary data transfers
  public send(chunk: string | ArrayBuffer | ArrayBufferView | Blob) {
    if (this.isDestroyedOrDestroying() || !this.isOpen()) return;
    try {
      this.channel!.send(chunk as any);
    } catch (err) {
      console.warn('Failed to send on datachannel', err);
    }
  }

  private onInterval() {
    if (!this.channel || this.channel.bufferedAmount > MAX_BUFFERED_AMOUNT) {
      return;
    }
    this.onChannelBufferedAmountLow();
  }

  protected onDestroy(): void {
    this.peerConnection.removeEventListener('datachannel', this.onDataChannel);
    this.channel?.removeEventListener('message', this.onChannelMessage);
    this.channel?.removeEventListener('bufferedamountlow', this.onChannelBufferedAmountLow);
    this.channel?.removeEventListener('open', this.onChannelOpen);
    this.channel?.removeEventListener('close', this.onChannelClose);
    this.channel?.removeEventListener('error', this.onChannelError);

    this.channel?.close();

    clearInterval(this.closingInterval);
    clearInterval(this.interval);
    this.closingInterval = undefined;
    this.interval = undefined;
  }
}
